/* eslint-disable */
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Paper,
  Chip,
  Divider,
  Autocomplete,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  createTheme,
  ThemeProvider
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import { fetcher, postFetcher } from 'src/utils/axios'
import Iconify from 'src/components/iconify'

// ── Theme matching the "Create Node Template" dialog color scheme ──────────────
const dialogTheme = createTheme({
  palette: {
    primary: {
      main: '#6366f1',       // indigo-blue from the active card border
      light: '#EEF0FD',      // very light indigo for selected bg
      dark: '#3A45C4',
      contrastText: '#ffffff',
    },
    success: {
      main: '#27AE60',
      light: '#E9F7EF',
    },
    grey: {
      50: '#F7F8FA',
      100: '#F0F1F5',
      200: '#E4E6EF',
      300: '#C8CBD8',
      600: '#6B7080',
      800: '#2D2F3E',
      900: '#1A1B28',
    },
    background: {
      default: '#FFFFFF',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#1A1B28',
      secondary: '#6B7080',
    },
    divider: '#E4E6EF',
  },
  typography: {
    fontFamily: '"DM Sans", "Helvetica Neue", Arial, sans-serif',
    h6: { fontWeight: 700, letterSpacing: '-0.3px' },
    subtitle2: { fontWeight: 600 },
    body2: { fontSize: '0.825rem' },
    caption: { fontSize: '0.75rem' },
  },
  shape: { borderRadius: 10 },
  components: {
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          boxShadow: '0 24px 64px rgba(0,0,0,0.12)',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: '20px 24px 16px',
          borderBottom: '1px solid #E4E6EF',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: { padding: '20px 24px' },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '16px 24px 20px',
          borderTop: '1px solid #E4E6EF',
          gap: 8,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            backgroundColor: '#FFFFFF',
            '& fieldset': { borderColor: '#E4E6EF' },
            '&:hover fieldset': { borderColor: '#C8CBD8' },
            '&.Mui-focused fieldset': { borderColor: '#6366f1', borderWidth: 1.5 },
          },
          '& .MuiInputLabel-root': { color: '#6B7080' },
          '& .MuiInputLabel-root.Mui-focused': { color: '#6366f1' },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.875rem',
          padding: '9px 20px',
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
        },
        containedPrimary: {
          background: '#6366f1',
          '&:hover': { background: '#3A45C4' },
        },
        containedSuccess: {
          background: '#27AE60',
          '&:hover': { background: '#1E8A4A' },
        },
        outlinedInherit: {
          border: '1px solid #E4E6EF',
          color: '#2D2F3E',
          '&:hover': { background: '#F7F8FA', border: '1px solid #C8CBD8' },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 6, fontWeight: 500, fontSize: '0.75rem' },
        outlinedDefault: { border: '1px solid #E4E6EF', background: '#F7F8FA' },
        outlinedPrimary: { border: '1px solid #C7CBFA', background: '#EEF0FD', color: '#6366f1' },
        colorError: { background: '#FEE8E8', color: '#D32F2F', border: '1px solid #FAC5C5' },
        colorInfo: { background: '#E8F4FD', color: '#0277BD', border: '1px solid #B3D9F5' },
        colorSuccess: { background: '#E9F7EF', color: '#27AE60', border: '1px solid #A8DAB5' },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: '#C8CBD8',
          '&.Mui-checked': { color: '#6366f1' },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        standardSuccess: {
          background: '#E9F7EF',
          color: '#1E5631',
          border: '1px solid #A8DAB5',
          borderRadius: 10,
        },
        standardError: {
          background: '#FEE8E8',
          color: '#6B0000',
          border: '1px solid #FAC5C5',
          borderRadius: 10,
        },
      },
    },
  },
})

// ── Styled sub-components ─────────────────────────────────────────────────────

const SectionLabel = ({ children }) => (
  <Typography
    variant="caption"
    sx={{
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      fontWeight: 700,
      color: '#6B7080',
      display: 'flex',
      alignItems: 'center',
      gap: 0.75,
      mb: 1.5,
    }}
  >
    {children}
  </Typography>
)

const InfoCard = ({ children, highlight }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      borderRadius: 2,
      border: '1px solid',
      borderColor: highlight ? '#C7CBFA' : '#E4E6EF',
      bgcolor: highlight ? '#EEF0FD' : '#F7F8FA',
      mb: 2,
    }}
  >
    {children}
  </Paper>
)

const TypeCard = ({ value, selected, onChange, icon, title, subtitle }) => (
  <Paper
    elevation={0}
    onClick={() => onChange(value)}
    sx={{
      flex: 1,
      p: 2,
      borderRadius: 2,
      border: '1.5px solid',
      borderColor: selected ? '#6366f1' : '#E4E6EF',
      bgcolor: selected ? '#EEF0FD' : '#FFFFFF',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      transition: 'all 0.15s ease',
      '&:hover': {
        borderColor: selected ? '#6366f1' : '#C8CBD8',
        bgcolor: selected ? '#EEF0FD' : '#F7F8FA',
      },
      position: 'relative',
    }}
  >
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: 1.5,
        bgcolor: selected ? '#6366f1' : '#E4E6EF',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: selected ? '#fff' : '#6B7080',
        fontSize: 18,
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box>
      <Typography variant="subtitle2" sx={{ color: selected ? '#6366f1' : '#1A1B28' }}>
        {title}
      </Typography>
      <Typography variant="caption" sx={{ color: '#6B7080' }}>
        {subtitle}
      </Typography>
    </Box>
    {selected && (
      <Box
        sx={{
          position: 'absolute',
          top: 10,
          right: 10,
          width: 20,
          height: 20,
          borderRadius: '50%',
          bgcolor: '#6366f1',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          fontSize: 12,
        }}
      >
        ✓
      </Box>
    )}
  </Paper>
)

const SummaryRow = ({ label, value, large, accent }) => (
  <Box>
    <Typography variant="caption" sx={{ color: '#6B7080', display: 'block', mb: 0.25 }}>
      {label}
    </Typography>
    <Typography
      variant={large ? 'h5' : 'body1'}
      fontWeight={large ? 700 : 500}
      sx={{ color: accent ? '#6366f1' : '#1A1B28' }}
    >
      {value}
    </Typography>
  </Box>
)

// ── Main component ────────────────────────────────────────────────────────────
function AllocableCostDialog({ open, onClose, data, onDecision }) {
  if (!data || !data.nodes?.length) return null;

  // Key = `${node_id}-${variant_id}`
  const [selections, setSelections] = useState(() => {
    const initial = {};
    data.nodes.forEach(node => {
      node.product_variants?.forEach(variant => {
        if (variant.is_allocable) {
          initial[`${node.node_id}-${variant.id}`] = true;
        }
      });
    });
    return initial;
  });

  const [globalSelection, setGlobalSelection] = useState('all');

  const allocableVariants = [];
  data.nodes.forEach(node => {
    node.product_variants?.forEach(variant => {
      if (variant.is_allocable) {
        allocableVariants.push({ nodeId: node.node_id, variantId: variant.id });
      }
    });
  });

  const allSelected = allocableVariants.every(
    v => selections[`${v.nodeId}-${v.variantId}`] === true
  );
  const noneSelected = allocableVariants.every(
    v => selections[`${v.nodeId}-${v.variantId}`] === false
  );

  useEffect(() => {
    if (allSelected) setGlobalSelection('all');
    else if (noneSelected) setGlobalSelection('none');
    else setGlobalSelection('mixed');
  }, [selections]);

  const handleGlobalChange = (e) => {
    const value = e.target.value;
    setGlobalSelection(value);

    if (value === 'all' || value === 'none') {
      const include = value === 'all';
      setSelections(prev => {
        const next = { ...prev };
        allocableVariants.forEach(({ nodeId, variantId }) => {
          next[`${nodeId}-${variantId}`] = include;
        });
        return next;
      });
    }
  };

  const handleVariantChange = (nodeId, variantId) => (e) => {
    const include = e.target.value === 'yes';
    setSelections(prev => ({
      ...prev,
      [`${nodeId}-${variantId}`]: include,
    }));
  };

  const handleConfirm = () => {
    const payload = [];
    data.nodes.forEach(node => {
      node.product_variants?.forEach(variant => {
        if (variant.is_allocable) {
          const key = `${node.node_id}-${variant.id}`;
          payload.push({
            node_id: Number(node.node_id),
            product_variant_id: Number(variant.id),
            is_cost: !!selections[key],
          });
        }
      });
    });

    onDecision(payload);
    onClose();
  };

  const allocableCount = data.nodes.reduce((sum, node) => {
    return sum + (node.product_variants?.filter(v => v.is_allocable)?.length || 0);
  }, 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Include Allocable Costs?
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {allocableCount} allocable cost item(s) found in this workflow
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {/* Global control */}
        <Paper sx={{ p: 2, mb: 3, bgcolor: '#f8f9ff', border: '1px solid #d1d9ff' }}>
          <FormControl component="fieldset">
            <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600 }}>
              Select all / deselect all allocable variants
            </FormLabel>
            <RadioGroup row value={globalSelection} onChange={handleGlobalChange}>
              <FormControlLabel value="all" control={<Radio />} label="Include cost for all" />
              <FormControlLabel value="none" control={<Radio />} label="Exclude cost from all" />
            </RadioGroup>
          </FormControl>
        </Paper>

        <Divider sx={{ my: 3 }} />

        {/* Per-node → Per-variant selections */}
        {data.nodes.map((node) => (
          <Paper
            key={node.node_id}
            elevation={0}
            sx={{
              p: 2.5,
              mb: 2.5,
              border: '1px solid #e2e8f0',
              borderRadius: 2,
            }}
          >
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              {node.name} <Typography component="span" variant="caption" color="text.secondary">
                (Node ID: {node.node_id}) • {node.type}
              </Typography>
            </Typography>

            <Divider sx={{ my: 1.5 }} />

            <Box sx={{ pl: 1 }}>
              {node.product_variants?.map((pv) => (
                <Box
                  key={pv.id}
                  sx={{
                    p: 1.5,
                    mb: 1.5,
                    borderRadius: 1.5,
                    bgcolor: pv.is_allocable ? '#e0f2fe' : '#f1f5f9',
                    border: pv.is_allocable ? '1px solid #93c5fd' : '1px solid #cbd5e1',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        Variant <strong>{pv.id}</strong>
                      </Typography>
                      {pv.attributes && (
                        <Typography variant="caption" color="text.secondary">
                          {Object.entries(pv.attributes)
                            .map(([k, v]) => `${k}: ${v}`)
                            .join(' • ')}
                        </Typography>
                      )}
                    </Box>

                    {pv.is_allocable ? (
                      <FormControl component="fieldset">
                        <RadioGroup
                          row
                          value={selections[`${node.node_id}-${pv.id}`] ? 'yes' : 'no'}
                          onChange={handleVariantChange(node.node_id, pv.id)}
                        >
                          <FormControlLabel
                            value="yes"
                            control={<Radio color="primary" />}
                            label="Include cost"
                          />
                          <FormControlLabel
                            value="no"
                            control={<Radio color="default" />}
                            label="Skip cost"
                          />
                        </RadioGroup>
                      </FormControl>
                    ) : (
                      <Chip label="Non-allocable" size="small" variant="outlined" />
                    )}
                  </Box>

                  {pv.is_allocable && (
                    <Box sx={{ mt: 1 }}>
                      <Chip
                        label={`Cost: ₹ ${pv.cost?.toLocaleString() || '—'}`}
                        color="success"
                        size="medium"
                        sx={{ fontWeight: 600 }}
                      />
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          </Paper>
        ))}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleConfirm}
          startIcon={<span role="img" aria-label="check">✓</span>}
        >
          Confirm Selection
        </Button>
      </DialogActions>
    </Dialog>
  );
}
export default function CalculateDialog({
  CalculateDialogOpen,
  handleCloseCalculateDialog,
  workflowId,
  calcualtionneededfields,
  selectedmasterworkflow,
  handleUpdateNode,
  isUpdatingNodes,
  onUpdateAllNodes,
}) {
  const [quantity, setQuantity] = useState('')
  const [attributeValues, setAttributeValues] = useState({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [results, setResults] = useState(null)
  const [variants, setVariants] = useState([])
  const [productVariants, setProductVariants] = useState([])
  const [finalOutputData, setFinalOutputData] = useState([])
  const [fetchingVariants, setFetchingVariants] = useState(false)
  const [productid, setproductid] = useState()
  const [aggregateData, setAggregateData] = useState([])
  const [aggregateValues, setAggregateValues] = useState({})
  const [calculationType, setcalculationType] = useState('Backtrack')
  const [allocableData, setAllocableData] = useState(null);
  const [showAllocableDialog, setShowAllocableDialog] = useState(false);
  const [wantToIncludeAllocableCost, setWantToIncludeAllocableCost] = useState(false);
  const [allocableSelection, setAllocableSelection] = useState(null); // will be array or null
  React.useEffect(() => {
    if (calcualtionneededfields?.attributes) {
      const initialValues = {}
      calcualtionneededfields.attributes.forEach(attr => {
        initialValues[attr.attribute] = ''
      })
      setAttributeValues(initialValues)
    }
  }, [calcualtionneededfields])

  const fetchVariantsForProducts = async (productIds) => {
    if (!productIds || productIds.length === 0) { setVariants([]); return }
    setFetchingVariants(true)
    try {
      const variantPromises = productIds.map(id => fetcher([`/new/workflow-app/products/variants/list/${id}/`]))
      const variantResults = await Promise.all(variantPromises)
      const allVariants = variantResults.flat().map(item => ({
        label: item.name,
        value: item.id.toString(),
        variant: { id: item.id, name: item.name },
      }))
      setVariants(allVariants)
    } catch (e) {
      console.error('Error fetching variants:', e)
      setVariants([])
    } finally {
      setFetchingVariants(false)
    }
  }

  const handlefetchaggregate = async () => {
    try {
      setLoading(true)
      if (workflowId) {
        const res = await fetcher([`/new/workflow-app/workflow/outputs/aggregable-quantities/?workflow_creation_id=${workflowId}`])
        setAggregateData(res)
        const init = {}
        res.forEach(item => item.aggregable_keys.forEach(key => { init[`${item.product_id}.${key}`] = '' }))
        setAggregateValues(init)
      }
    } catch (e) {
      setError('Failed to fetch aggregate data')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { handlefetchaggregate() }, [workflowId, CalculateDialogOpen])

  const handleAggregateValueChange = (key, value) =>
    setAggregateValues(prev => ({ ...prev, [key]: parseFloat(value) }))

  const handlefetchfinal = async () => {
    if (selectedmasterworkflow) {
      try {
        const res = await fetcher([`/new/workflow-app/output/final/quantity/using/processcreation/id/?process_creation_id=${selectedmasterworkflow}`])
        setFinalOutputData(res)
        const noVariants = res.filter(o => o.product_variants?.length === 0)
        if (noVariants.length > 0) {
          const ids = noVariants.flatMap(o => o.products.map(p => p.id))
          fetchVariantsForProducts(ids)
          setproductid(ids)
        } else {
          setVariants([])
        }
      } catch (e) {
        setFinalOutputData([])
        setVariants([])
      }
    }
  }

  useEffect(() => { handlefetchfinal() }, [selectedmasterworkflow, CalculateDialogOpen])

  // const handleCalculate = async () => {
  //   if (calculationType === 'Fronttrack') {
  //     const missing = Object.keys(aggregateValues).filter(k => !aggregateValues[k])
  //     if (missing.length > 0) return setError(`Please enter values for: ${missing.join(', ')}`)
  //   }
  //   setLoading(true); setError(''); setResults(null)
  //   try {
  //     const final_quantity = { product_variant_id: productVariants[0]?.variant?.id }
  //     const payload = { workflow_creation_id: workflowId, final_quantity }
  //     if (calculationType === 'Backtrack') payload.backtrack_target = parseFloat(quantity)
  //     if (calculationType === 'Fronttrack') payload.aggregable_values = aggregateValues
  //     const res = await postFetcher([calculationType === 'Backtrack' ? '/new/workflow-app/backtrack/' : '/new/workflow-app/fronttrack/', payload])
  //     setResults(res)
  //   } catch (err) {
  //     setError(err.message || 'Failed to calculate. Please try again.')
  //   } finally {
  //     setLoading(false)
  //   }
  // }       

  const runMainCalculation = async (allocableArray) => {
    try {
      setLoading(true);

      const final_quantity = { product_variant_id: productVariants[0]?.variant?.id };

      const payload = {
        workflow_creation_id: workflowId,
        final_quantity,
        is_allocable: allocableArray,   // ← now array of {node_id, is_cost}
      };

      if (calculationType === 'Backtrack') {
        payload.backtrack_target = parseFloat(quantity);
      }
      if (calculationType === 'Fronttrack') {
        payload.aggregable_values = aggregateValues;
      }

      const endpoint =
        calculationType === 'Backtrack'
          ? '/new/workflow-app/backtrack/'
          : '/new/workflow-app/fronttrack/';

      const res = await postFetcher([endpoint, payload]);
      setResults(res);
    } catch (err) {
      setError(err.message || 'Calculation failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleCalculate = async () => {
    if (calculationType === 'Fronttrack') {
      const missing = Object.keys(aggregateValues).filter(k => !aggregateValues[k]);
      if (missing.length > 0) {
        return setError(`Please enter values for: ${missing.join(', ')}`);
      }
    }
    setLoading(true);
    setError('');
    setResults(null);
    setAllocableData(null);
    setShowAllocableDialog(false);

    try {
      const allocRes = await fetcher([
        `/new/workflow-app/cost/allocable/dialog/?workflow_creation_id=${workflowId}`
      ]);
      setAllocableData(allocRes);

      const hasAnyAllocable = allocRes?.nodes?.some(node =>
        node.product_variants?.some(v => v.is_allocable === true)
      ) ?? false;

      if (hasAnyAllocable) {
        setShowAllocableDialog(true);
        setLoading(false);
        return;
      }

      // No allocable → empty array or omit
      await runMainCalculation([]); // or omit the field

    } catch (err) {
      setError(err.message || 'Failed to prepare calculation.');
      setLoading(false);
    }
  };

  const handleClose = () => {
    setQuantity(''); setAttributeValues({}); setAggregateValues({}); setAggregateData({})
    setError(''); setResults(null); setFinalOutputData([]); setProductVariants([]); setVariants([])
    handleCloseCalculateDialog()
  }

  const handleKeyPress = (e) => { if (e.key === 'Enter' && !loading) handleCalculate() }

  const hasAttributes = calcualtionneededfields?.attributes?.length > 0
  const showVariantAutocomplete = variants.length > 0

  return (
    <ThemeProvider theme={dialogTheme}>
      <Dialog
        open={CalculateDialogOpen}
        onClose={loading ? undefined : handleClose}
        maxWidth="md"
        fullWidth
      >
        {/* ── Title ── */}
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1.5,
                bgcolor: '#EEF0FD',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 18,
              }}
            >
              ⚙️
            </Box>
            <Box>
              <Typography variant="h6" sx={{ color: '#1A1B28', lineHeight: 1.2 }}>
                Calculate {calculationType}
              </Typography>
              <Typography variant="caption" sx={{ color: '#6B7080' }}>
                Run workflow quantity calculations
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleClose} size="small" disabled={loading}
            sx={{ color: '#6B7080', bgcolor: '#F7F8FA', borderRadius: 1.5, '&:hover': { bgcolor: '#E4E6EF' } }}>
            <span style={{ fontSize: 16, lineHeight: 1 }}>✕</span>
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {!results ? (
            <Box>
              {/* ── Calculation Type ── */}
              <Box sx={{ mb: 3 }}>
                <SectionLabel>📐 Calculation Type</SectionLabel>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <TypeCard
                    value="Backtrack"
                    selected={calculationType === 'Backtrack'}
                    onChange={setcalculationType}
                    icon="←"
                    title="Backtrack"
                    subtitle="Target → Raw materials"
                  />
                  <TypeCard
                    value="Fronttrack"
                    selected={calculationType === 'Fronttrack'}
                    onChange={setcalculationType}
                    icon="→"
                    title="Fronttrack"
                    subtitle="Raw input → Final output"
                  />
                </Box>
              </Box>

              <Divider sx={{ borderColor: '#E4E6EF', mb: 3 }} />

              {/* ── Output Data ── */}
              {finalOutputData.length > 0 ? (
                <Box sx={{ mb: 3 }}>
                  <SectionLabel>📦 Output Configuration</SectionLabel>
                  {finalOutputData.map((output, index) => (
                    <InfoCard key={index}>
                      <Typography variant="subtitle2" sx={{ color: '#1A1B28', mb: 1.5 }}>
                        {output.output_name}
                      </Typography>
                      <Box sx={{ mb: output.product_variants?.length > 0 ? 1.5 : 0 }}>
                        <Typography variant="caption" sx={{ color: '#6B7080', display: 'block', mb: 0.75 }}>
                          Products
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                          {output.products.map(product => (
                            <Chip key={product.id} label={`${product.name} · ${product.id}`} size="small" variant="outlined" />
                          ))}
                        </Box>
                      </Box>

                      {output.product_variants?.length > 0 && (
                        <Box sx={{ mb: 1.5 }}>
                          <Typography variant="caption" sx={{ color: '#6B7080', display: 'block', mb: 0.75 }}>
                            Variants
                          </Typography>
                          {output.product_variants.map(variant => (
                            <Box key={variant.id} sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                              <Chip label={`ID: ${variant.id}`} size="small" variant="outlined" />
                              {variant.attributes && Object.entries(variant.attributes).map(([k, v]) => (
                                <Chip key={k} label={`${k}: ${v}`} size="small" color="primary" variant="outlined" />
                              ))}
                            </Box>
                          ))}
                        </Box>
                      )}

                      <Box>
                        <Typography variant="caption" sx={{ color: '#6B7080', display: 'block', mb: 0.75 }}>
                          Categories
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                          {output.product_categories.map(cat => (
                            <Chip key={cat.id} label={`${cat.name} · ${cat.id}`} size="small" variant="outlined" />
                          ))}
                        </Box>
                      </Box>
                    </InfoCard>
                  ))}

                  {showVariantAutocomplete && (
                    <Autocomplete
                      multiple
                      options={variants}
                      getOptionLabel={o => o.label}
                      value={productVariants}
                      isOptionEqualToValue={(o, v) => o.value === v.value}
                      onChange={(_, val) => setProductVariants(val)}
                      renderInput={params => (
                        <TextField
                          {...params}
                          label="Select Variants"
                          helperText="Select variants for the products"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {fetchingVariants && <CircularProgress color="inherit" size={18} />}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      fullWidth
                      disabled={fetchingVariants}
                    />
                  )}
                </Box>
              ) : (
                <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1, color: '#6B7080' }}>
                  <CircularProgress size={14} sx={{ color: '#C8CBD8' }} />
                  <Typography variant="body2">Loading output data…</Typography>
                </Box>
              )}

              {/* ── Fronttrack: Aggregable Values ── */}
              {calculationType === 'Fronttrack' && aggregateData.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <SectionLabel>🔢 Aggregable Values</SectionLabel>
                  <InfoCard>
                    <Typography variant="body2" sx={{ color: '#6B7080', mb: 2 }}>
                      Provide values for the following aggregable keys:
                    </Typography>
                    {aggregateData.map((item, index) => (
                      <Box key={index} sx={{ mb: 2 }}>
                        <Typography variant="body2" fontWeight={600} sx={{ mb: 1, color: '#1A1B28' }}>
                          {item.output}
                          <Typography component="span" variant="caption" sx={{ color: '#6B7080', ml: 1 }}>
                            Product #{item.product_id}
                          </Typography>
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                          {item.aggregable_keys.map(key => {
                            const uniqueKey = `${item.product_id}.${key}`
                            return (
                              <TextField
                                key={uniqueKey}
                                type="number"
                                label={key}
                                placeholder={`Enter ${key}`}
                                InputProps={{ inputProps: { min: 0, step: 'any' } }}
                                value={aggregateValues[uniqueKey] ?? ''}
                                onChange={e => handleAggregateValueChange(uniqueKey, e.target.value)}
                                disabled={loading}
                                sx={{ minWidth: 160, flex: 1 }}
                                inputMode="decimal"
                              />
                            )
                          })}
                        </Box>
                      </Box>
                    ))}
                  </InfoCard>
                </Box>
              )}

              {/* ── Backtrack: Target Quantity ── */}
              {calculationType === 'Backtrack' && (
                <Box sx={{ mb: 1 }}>
                  <SectionLabel>🎯 Target Quantity</SectionLabel>
                  <TextField
                    type="number"
                    label="Target Quantity"
                    fullWidth
                    placeholder="Enter quantity"
                    InputProps={{ inputProps: { min: 0, step: 1 } }}
                    value={quantity}
                    onChange={e => { setQuantity(e.target.value); setError('') }}
                    onKeyPress={handleKeyPress}
                    disabled={loading}
                    error={!!error}
                    helperText={error || 'Number of units to produce'}
                    autoFocus
                  />
                </Box>
              )}

              {/* Fronttrack error */}
              {error && calculationType === 'Fronttrack' && (
                <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>
              )}
            </Box>
          ) : (
            /* ─────────────── RESULTS VIEW ─────────────── */
            <Box>
              <Alert severity="success" sx={{ mb: 3 }}>
                {calculationType === 'Backtrack'
                  ? `Backtrack complete — Target: ${quantity} units → Raw materials calculated`
                  : `Fronttrack complete — Final output: ${results.summary?.final_product?.final_computed_quantity || 'N/A'} pcs`}
              </Alert>

              {/* Summary Card */}
              <Box sx={{ mb: 3 }}>
                <SectionLabel>📊 Summary</SectionLabel>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    border: '1.5px solid #C7CBFA',
                    bgcolor: '#EEF0FD',
                  }}
                >
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
                    <SummaryRow label="Workflow" value={results.summary?.workflow?.name || 'N/A'} />
                    <SummaryRow
                      label="Final Output Node"
                      value={`${results.summary?.final_product?.node_name || 'N/A'} → ${results.summary?.final_product?.output_name}`}
                    />
                    <SummaryRow
                      label={calculationType === 'Backtrack' ? 'Target Quantity' : 'Final Computed Quantity'}
                      value={`${calculationType === 'Backtrack' ? quantity : results.summary?.final_product?.final_computed_quantity || 0} pcs`}
                      large accent
                    />
                    <SummaryRow label="Total Steps" value={results.summary?.total_steps || 0} />
                  </Box>

                  {results.summary?.raw_material_requirements?.length > 0 && (
                    <Box sx={{ mt: 2.5, pt: 2.5, borderTop: '1px solid #C7CBFA' }}>
                      <Typography variant="subtitle2" sx={{ mb: 1.5, color: '#1A1B28' }}>
                        Raw Materials Required
                      </Typography>
                      {results.summary.raw_material_requirements.map((mat, i) => (
                        <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                          <Typography variant="body2" sx={{ color: '#2D2F3E' }}>
                            Product <strong>#{mat.product_id}</strong> → {mat.attribute_key}
                          </Typography>
                          <Typography variant="body2" fontWeight={700} sx={{ color: '#27AE60' }}>
                            {mat.required_value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}

                  {results.summary?.raw_materials_used?.length > 0 && (
                    <Box sx={{ mt: 2.5, pt: 2.5, borderTop: '1px solid #C7CBFA' }}>
                      <Typography variant="subtitle2" sx={{ mb: 1.5, color: '#1A1B28' }}>
                        Raw Materials Used (Input)
                      </Typography>
                      {results.summary.raw_materials_used.map((mat, i) => (
                        <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                          <Typography variant="body2" sx={{ color: '#2D2F3E' }}>
                            Product <strong>#{mat.product_id}</strong> → {mat.attribute_key}
                          </Typography>
                          <Typography variant="body2" fontWeight={700} sx={{ color: '#27AE60' }}>
                            {mat.input_value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Paper>
              </Box>

              {/* Step-by-step Breakdown */}
              <Box>
                <SectionLabel>🔍 Step-by-Step Breakdown ({calculationType})</SectionLabel>
                <Paper variant="outlined" sx={{ borderColor: '#E4E6EF', borderRadius: 2, overflow: 'hidden' }}>
                  <Box sx={{ overflowX: 'auto', minWidth: 600 }}>
                    {results.breakdown?.map((step, index) => (
                      <Box
                        key={step.output_id}
                        sx={{
                          p: 2,
                          borderBottom: index < results.breakdown.length - 1 ? '1px solid #E4E6EF' : 'none',
                          bgcolor: step.is_final_output ? '#E9F7EF' : index % 2 === 0 ? '#FFFFFF' : '#F7F8FA',
                          position: 'relative',
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Box sx={{
                                width: 22, height: 22, borderRadius: '50%', bgcolor: '#E4E6EF',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '0.7rem', fontWeight: 700, color: '#6B7080', flexShrink: 0,
                              }}>
                                {index + 1}
                              </Box>
                              <Typography variant="subtitle2" sx={{ color: '#1A1B28' }}>
                                {step.node?.name || 'Unnamed Node'}
                              </Typography>
                            </Box>
                            <Typography variant="caption" sx={{ color: '#6B7080', ml: 3.75, display: 'block' }}>
                              Output: {step.output_name} (ID: {step.output_id})
                            </Typography>

                            {step.formula && step.formula !== 'Pass-through' && step.formula !== 'Pass-through (auto-resolved)' && (
                              <Box sx={{
                                mt: 0.75, ml: 3.75, px: 1.25, py: 0.5, borderRadius: 1,
                                bgcolor: '#F0F1F5', display: 'inline-block',
                                fontFamily: 'monospace', fontSize: '0.78rem', color: '#6366f1',
                              }}>
                                {typeof step.formula === 'object' ? JSON.stringify(step.formula) : step.formula}
                              </Box>
                            )}

                            {step.node?.inputs?.length > 0 && (
                              <Box sx={{ mt: 0.75, ml: 3.75, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {step.node.inputs.map((inp, i) => (
                                  <Chip
                                    key={i}
                                    label={inp.name}
                                    size="small"
                                    color={inp.is_material ? 'error' : 'info'}
                                    variant="outlined"
                                  />
                                ))}
                              </Box>
                            )}
                          </Box>

                          <Box sx={{ textAlign: 'right', ml: 2, flexShrink: 0 }}>
                            <Typography
                              variant="h5"
                              fontWeight={700}
                              sx={{ color: step.is_final_output ? '#27AE60' : '#6366f1' }}
                            >
                              {calculationType === 'Backtrack' ? step.required_quantity : step.computed_quantity}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#6B7080' }}>
                              {step.unit || 'pcs'}
                            </Typography>
                          </Box>
                        </Box>

                        {step.is_final_output && (
                          <Chip
                            label="FINAL OUTPUT"
                            color="success"
                            size="small"
                            sx={{ position: 'absolute', top: 10, right: 10 }}
                          />
                        )}
                      </Box>
                    ))}
                  </Box>
                </Paper>
              </Box>
            </Box>
          )}
        </DialogContent>

        {/* ── Actions ── */}
        <DialogActions>
          {!results ? (
            <>
              <Button onClick={handleClose} disabled={loading} variant="outlined" color="inherit">
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleCalculate}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
                sx={{ minWidth: 130 }}
              >
                {loading ? 'Calculating…' : 'Calculate'}
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={() => { setResults(null); setQuantity(''); setAttributeValues({}) }}
                variant="outlined"
                color="inherit"
              >
                New Calculation
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => onUpdateAllNodes(results)}
                disabled={loading || isUpdatingNodes}
                startIcon={isUpdatingNodes
                  ? <CircularProgress size={16} color="inherit" />
                  : <Iconify icon="eva:save-fill" />
                }
                sx={{ px: 3 }}
              >
                {isUpdatingNodes ? 'Updating…' : 'Update All Nodes'}
              </Button>
              <Button variant="contained" color="primary" onClick={handleClose}>
                Done
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <AllocableCostDialog
        open={showAllocableDialog}
        onClose={() => {
          setShowAllocableDialog(false);
          setLoading(false);
        }}
        data={allocableData}
        onDecision={(allocableArray) => {
          runMainCalculation(allocableArray);
        }}
      />
    </ThemeProvider>
  )
  }